import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Fade, Zoom } from '@mui/material';
import { Close, EmojiEvents, Star, Celebration } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import notificationSound from '../../services/notificationSoundService';
import SwipeableNotification from '../Notifications/SwipeableNotification';

const PremiumAchievementNotification = ({ 
  achievement, 
  onClose, 
  show = true 
}) => {
  const theme = useTheme();
  const [visible, setVisible] = useState(false);
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    if (show) {
      setVisible(true);
      notificationSound.play('achievement');
      // Generate particles for celebration effect
      const newParticles = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        x: Math.random() * 400,
        y: Math.random() * 300,
        delay: Math.random() * 2,
        duration: 2 + Math.random() * 2
      }));
      setParticles(newParticles);

      // Auto-close after 8 seconds
      const timer = setTimeout(() => {
        handleClose();
      }, 8000);

      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [show]);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => onClose?.(), 300);
  };

  if (!achievement) return null;

  const isDark = theme.palette.mode === 'dark';
  
  // Rarity-based colors
  const getRarityColors = (rarity) => {
    const colors = {
      common: {
        primary: isDark ? '#9E9E9E' : '#757575',
        secondary: isDark ? '#BDBDBD' : '#9E9E9E',
        glow: isDark ? 'rgba(158, 158, 158, 0.3)' : 'rgba(117, 117, 117, 0.2)'
      },
      uncommon: {
        primary: isDark ? '#4CAF50' : '#388E3C',
        secondary: isDark ? '#66BB6A' : '#4CAF50',
        glow: isDark ? 'rgba(76, 175, 80, 0.4)' : 'rgba(56, 142, 60, 0.3)'
      },
      rare: {
        primary: isDark ? '#2196F3' : '#1976D2',
        secondary: isDark ? '#42A5F5' : '#2196F3',
        glow: isDark ? 'rgba(33, 150, 243, 0.4)' : 'rgba(25, 118, 210, 0.3)'
      },
      epic: {
        primary: isDark ? '#9C27B0' : '#7B1FA2',
        secondary: isDark ? '#BA68C8' : '#9C27B0',
        glow: isDark ? 'rgba(156, 39, 176, 0.4)' : 'rgba(123, 31, 162, 0.3)'
      },
      legendary: {
        primary: isDark ? '#FFD700' : '#FF8F00',
        secondary: isDark ? '#FFA500' : '#FFB300',
        glow: isDark ? 'rgba(255, 215, 0, 0.4)' : 'rgba(255, 143, 0, 0.3)'
      },
      mythic: {
        primary: isDark ? '#FF4081' : '#C2185B',
        secondary: isDark ? '#FF80AB' : '#E91E63',
        glow: isDark ? 'rgba(255, 64, 129, 0.4)' : 'rgba(194, 24, 91, 0.3)'
      }
    };
    return colors[rarity?.toLowerCase()] || colors.common;
  };
  
  const rarityColors = getRarityColors(achievement.rarity);
  
  const containerStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    backdropFilter: 'blur(10px)',
  };

  const cardStyle = {
    position: 'relative',
    width: '400px',
    maxWidth: '90vw',
    padding: '32px',
    borderRadius: '24px',
    background: isDark 
      ? `linear-gradient(135deg, ${rarityColors.primary}20 0%, ${rarityColors.secondary}10 50%, rgba(0, 0, 0, 0.9) 100%)`
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 255, 0.9) 100%)',
    border: `2px solid ${rarityColors.primary}50`,
    boxShadow: `0 20px 40px ${rarityColors.glow}, 0 0 60px ${rarityColors.primary}20`,
    overflow: 'hidden',
  };

  const glowStyle = {
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: `radial-gradient(circle, ${rarityColors.primary}20 0%, transparent 70%)`,
    animation: 'pulse 3s ease-in-out infinite',
  };

  return (
    <AnimatePresence>
      {show && visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={containerStyle}
        >
          {/* Celebration Particles */}
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              initial={{ 
                opacity: 0, 
                scale: 0,
                x: particle.x,
                y: particle.y,
                rotate: 0
              }}
              animate={{ 
                opacity: [0, 1, 0], 
                scale: [0, 1, 0],
                y: particle.y - 100,
                rotate: 360
              }}
              transition={{ 
                duration: particle.duration,
                delay: particle.delay,
                ease: "easeOut"
              }}
              style={{
                position: 'absolute',
                width: '8px',
                height: '8px',
                background: rarityColors.primary,
                borderRadius: '50%',
              }}
            />
          ))}

          <SwipeableNotification onDismiss={handleClose} showCloseButton={false}>
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0, rotate: 180 }}
              transition={{ 
                type: "spring", 
                stiffness: 200, 
                damping: 20,
                duration: 0.6
              }}
              style={cardStyle}
            >
              {/* Glow Effect */}
              <div style={glowStyle} />
            
            {/* Close Button */}
            <IconButton
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: `${rarityColors.primary}B3`,
                '&:hover': {
                  color: rarityColors.primary,
                }
              }}
            >
              <Close />
            </IconButton>

            {/* Achievement Icon */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 2,
              position: 'relative'
            }}>
              <motion.div
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ 
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Box sx={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${rarityColors.primary} 0%, ${rarityColors.secondary} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 8px 32px ${rarityColors.glow}`,
                }}>
                  <EmojiEvents sx={{ 
                    fontSize: 40, 
                    color: 'white',
                  }} />
                </Box>
              </motion.div>
              
              {/* Floating Stars */}
              {[...Array(3)].map((_, i) => (
                <motion.div
                  key={i}
                  animate={{
                    y: [-10, -20, -10],
                    opacity: [0.5, 1, 0.5],
                    scale: [0.8, 1.2, 0.8]
                  }}
                  transition={{
                    duration: 2,
                    delay: i * 0.3,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  style={{
                    position: 'absolute',
                    top: `${20 + i * 15}px`,
                    left: `${100 + i * 30}px`,
                  }}
                >
                  <Star sx={{ 
                    color: rarityColors.primary,
                    fontSize: 16 + i * 2
                  }} />
                </motion.div>
              ))}
            </Box>

            {/* Achievement Title */}
            <Typography
              variant="h4"
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                background: `linear-gradient(135deg, ${rarityColors.primary} 0%, ${rarityColors.secondary} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
                textShadow: `0 2px 10px ${rarityColors.glow}`,
              }}
            >
              Achievement Unlocked!
            </Typography>

            {/* Achievement Name */}
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                fontWeight: 600,
                color: rarityColors.primary,
                mb: 1,
              }}
            >
              {achievement.name || 'Test Achievement'}
            </Typography>

            {/* Rarity Badge */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box sx={{
                px: 2,
                py: 0.5,
                borderRadius: '12px',
                background: `${rarityColors.primary}15`,
                border: `1px solid ${rarityColors.primary}40`,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 'bold',
                    color: rarityColors.primary,
                    fontSize: '0.75rem'
                  }}
                >
                  {achievement.rarity || 'Common'}
                </Typography>
              </Box>
            </Box>

            {/* Achievement Description */}
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.7)',
                mb: 2,
                lineHeight: 1.5,
              }}
            >
              {achievement.description || 'You have successfully completed a milestone!'}
            </Typography>

            {/* Points Badge */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1
            }}>
              <Box sx={{
                px: 3,
                py: 1,
                borderRadius: '20px',
                background: `${rarityColors.primary}20`,
                border: `1px solid ${rarityColors.primary}30`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}>
                <Star sx={{ 
                  color: rarityColors.primary,
                  fontSize: 20
                }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 'bold',
                    color: rarityColors.primary,
                  }}
                >
                  +{achievement.points || 100} Points
                </Typography>
              </Box>
            </Box>

            {/* Celebration Icon */}
            <Box sx={{ 
              position: 'absolute',
              bottom: 16,
              right: 16,
            }}>
              <motion.div
                animate={{ rotate: [0, 15, -15, 0] }}
                transition={{ 
                  duration: 1,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <Celebration sx={{ 
                  color: `${rarityColors.primary}80`,
                  fontSize: 24
                }} />
              </motion.div>
            </Box>
            </motion.div>
          </SwipeableNotification>

          <style>{`
            @keyframes pulse {
              0%, 100% { transform: scale(1); opacity: 0.5; }
              50% { transform: scale(1.05); opacity: 0.8; }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Utility function to trigger notifications manually
export const triggerAchievementNotification = (achievement) => {
  const event = new CustomEvent('achievementUnlocked', { detail: achievement });
  window.dispatchEvent(event);
};

export default PremiumAchievementNotification;